/**
 * @fileoverview Client-only sign-in form that reads URL search params.
 * Used only from SignInForm when URL search params are not provided server-side; the parent is a
 * client component so useSearchParams() does not run during RSC/SSR for the SignInPage path.
 */
'use client';

import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

import type { SignInFormProps } from './SignInForm.types';
import { SignInFormContent } from './SignInFormContent';

export function SignInFormFromClientParams(props: SignInFormProps) {
  const searchParams = useSearchParams();
  const defaultCallback = props.defaultCallbackUrl ?? props.config?.routes?.homepage ?? '/home';
  const rawCallback = searchParams?.get('callbackUrl') ?? defaultCallback;
  const callbackUrl = useMemo(() => {
    try {
      if (rawCallback.startsWith('http://') || rawCallback.startsWith('https://')) {
        const target = new URL(rawCallback);
        return target.pathname + (target.search !== null && target.search !== undefined ? target.search : '');
      }
    } catch {
      // ignore
    }
    return rawCallback;
  }, [rawCallback]);
  const errorParam = ((v) => v !== null && v !== undefined ? v : '')(searchParams?.get('error'));
  const paramError = useMemo(() => {
    switch (errorParam) {
      case 'CredentialsSignin':
        return 'Invalid email or password. Please check your credentials and try again.';
      case 'Configuration':
        return 'There is a problem with the server configuration.';
      case 'AccessDenied':
        return 'Access denied. You do not have permission to sign in.';
      case 'Verification':
        return 'The verification token has expired or has already been used.';
      default:
        return errorParam ? 'An error occurred during authentication. Please try again.' : '';
    }
  }, [errorParam]);
  const showRegisteredSuccess = searchParams?.get('registered') === '1';

  return (
    <SignInFormContent
      config={props.config}
      defaultCallbackUrl={props.defaultCallbackUrl}
      className={props.className}
      callbackUrl={callbackUrl}
      paramError={paramError}
      showRegisteredSuccess={showRegisteredSuccess}
    />
  );
}
